// AIOE owns it; the workbench copies it (D441).
/**
 * The organisation's approved Microsoft Foundry connections, and the catalogue of Foundry agents people publish
 * for the organisation (D441). The connections are the administrators' list of where members may work: AIOE
 * writes the `foundry.projects` of the organisation's policy from them (schemas/foundry.ts, the workbench-owned
 * clause) so the workbench enforces what the console shows. The catalogue is versioned like the other catalogues:
 * versions are immutable, a held version waits for a reviewer, a withdrawn agent stays readable.
 *
 *   GET    /foundry/v1/connections                            { v: 1, connections } (ETag; a node sees only identity "organisation")
 *   GET    /foundry/v1/agents                                 FoundryCatalogue
 *   GET    /foundry/v1/agents/:agentId                        FoundryAgentListing, with every version
 *   POST   /foundry/v1/agents                                 FoundryPublishRequest → 201 | 200 FoundryPublishAnswer
 *   POST   /foundry/v1/agents/:agentId/versions/:version/review   { decision, note? }
 *   POST   /foundry/v1/agents/:agentId/withdraw               { reason? }
 *   POST   /foundry/v1/agents/:agentId/versions/:version/install  { as, teamId? } → { installed: true }
 *   POST   /foundry/v1/connections/requests                   a person asks the organisation to approve a project or deployment
 *   GET    /foundry/v1/connections/requests?mine=1            the caller's own requests
 *   GET/POST/PUT/DELETE /admin/v1/foundry/connections[/:id]   administrators
 *   GET    /admin/v1/foundry/connections/requests             administrators
 *   POST   /admin/v1/foundry/connections/requests/:id/decide  administrators
 *   GET    /admin/v1/foundry/agents                           administrators
 *
 * Plain zod, no Node.
 */
import { z } from "zod";
import { classificationSchema } from "./projects.js";
export const FOUNDRY_CONNECTIONS_PATH = "/foundry/v1/connections";
export const FOUNDRY_CONNECTION_REQUESTS_PATH = "/foundry/v1/connections/requests";
export const FOUNDRY_AGENTS_PATH = "/foundry/v1/agents";
export const ADMIN_FOUNDRY_CONNECTIONS_PATH = "/admin/v1/foundry/connections";
export const ADMIN_FOUNDRY_CONNECTION_REQUESTS_PATH = "/admin/v1/foundry/connections/requests";
export const ADMIN_FOUNDRY_AGENTS_PATH = "/admin/v1/foundry/agents";
export const FOUNDRY_CONNECTION_KINDS = ["azure", "azure-local"];
export const FOUNDRY_CONNECTION_IDENTITIES = ["person", "organisation"];
const guid = z.string().regex(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/, "a directory (tenant) id, a GUID");
const httpsUrl = z
    .string()
    .max(500)
    .refine((v) => {
    try {
        return new URL(v).protocol === "https:";
    }
    catch {
        return false;
    }
}, "an https address");
export const foundryResourceSchema = z.object({ id: z.string().min(1).max(500), name: z.string().min(1).max(100), region: z.string().min(1).max(60) });
export const foundryProjectSchema = z.object({
    endpoint: httpsUrl,
    name: z.string().min(1).max(100),
    resourceId: z.string().min(1).max(500).optional(),
    deployments: z.array(z.string().min(1).max(200)).max(100),
    hostedTools: z.array(z.string().min(1).max(100)).max(50).optional(),
});
export const foundryAzureLocalSchema = z.object({
    endpoint: httpsUrl,
    cluster: z.string().min(1).max(100),
    disconnected: z.boolean(),
    models: z.array(z.string().min(1).max(200)).max(100).optional(),
    agents: z.boolean().optional(),
});
const connectionFields = {
    kind: z.enum(FOUNDRY_CONNECTION_KINDS),
    name: z.string().trim().min(1).max(80),
    tenantId: guid.optional(),
    subscriptionIds: z.array(z.string().min(1).max(100)).max(50).optional(),
    resources: z.array(foundryResourceSchema).max(100).optional(),
    projects: z.array(foundryProjectSchema).max(100).optional(),
    azureLocal: foundryAzureLocalSchema.optional(),
    identity: z.enum(FOUNDRY_CONNECTION_IDENTITIES),
    /** The NAME of an organisation secret (never its value); only with identity "organisation". */
    organisationSecret: z.string().min(1).max(100).optional(),
    regions: z.array(z.string().min(1).max(60)).max(30).optional(),
};
/** Kind decides which parts a connection may hold; `organisationSecret` goes with the organisation's own identity. */
function checkConnection(v, ctx) {
    const bad = (path, message) => ctx.addIssue({ code: "custom", path: [path], message });
    if (v.kind === "azure") {
        if (!v.tenantId)
            bad("tenantId", "an Azure connection names its directory (tenant) id");
        if (v.azureLocal)
            bad("azureLocal", "an Azure connection has no Azure Local part");
    }
    else {
        if (!v.azureLocal)
            bad("azureLocal", "an Azure Local connection names its cluster and endpoint");
        if (v.projects)
            bad("projects", "an Azure Local connection has no Foundry projects");
        if (v.resources)
            bad("resources", "an Azure Local connection has no Azure resources");
        if (v.tenantId)
            bad("tenantId", "an Azure Local connection has no directory (tenant) id");
    }
    if (v.organisationSecret && v.identity !== "organisation")
        bad("organisationSecret", "a secret goes with the organisation's own identity");
}
export const foundryConnectionInputSchema = z.object(connectionFields).superRefine(checkConnection);
export const foundryConnectionSchema = z.object({ id: z.string().min(1).max(100), ...connectionFields, updatedAt: z.string(), updatedBy: z.string() }).superRefine(checkConnection);
export const foundryConnectionsAnswerSchema = z.object({ v: z.literal(1), connections: z.array(foundryConnectionSchema) });
// ── Asking the organisation to approve a project or deployment ─────────────────────────────
export const FOUNDRY_REQUEST_STATES = ["pending", "approved", "declined"];
export const foundryConnectionRequestInputSchema = z
    .object({
    kind: z.enum(FOUNDRY_CONNECTION_KINDS),
    tenantId: guid.optional(),
    project: foundryProjectSchema.optional(),
    azureLocal: foundryAzureLocalSchema.optional(),
    reason: z.string().trim().max(1000).optional(),
})
    .superRefine((v, ctx) => {
    if (v.kind === "azure") {
        if (!v.tenantId)
            ctx.addIssue({ code: "custom", path: ["tenantId"], message: "an Azure request names its directory (tenant) id" });
        if (!v.project)
            ctx.addIssue({ code: "custom", path: ["project"], message: "an Azure request names the Foundry project" });
        if (v.azureLocal)
            ctx.addIssue({ code: "custom", path: ["azureLocal"], message: "an Azure request has no Azure Local part" });
    }
    else {
        if (!v.azureLocal)
            ctx.addIssue({ code: "custom", path: ["azureLocal"], message: "an Azure Local request names its cluster and endpoint" });
        if (v.project || v.tenantId)
            ctx.addIssue({ code: "custom", path: ["project"], message: "an Azure Local request has no project or directory id" });
    }
});
/** One request as its person reads it. */
export const foundryConnectionRequestViewSchema = z.object({
    requestId: z.string(),
    kind: z.enum(FOUNDRY_CONNECTION_KINDS),
    endpoint: z.string(),
    state: z.enum(FOUNDRY_REQUEST_STATES),
    note: z.string().optional(),
    createdAt: z.string(),
    decidedAt: z.string().optional(),
});
/** One request as administrators read it: the view, who asked and what for. */
export const foundryConnectionRequestSchema = foundryConnectionRequestViewSchema.extend({
    askedBy: z.string(),
    askedByName: z.string().optional(),
    tenantId: z.string().optional(),
    project: foundryProjectSchema.optional(),
    azureLocal: foundryAzureLocalSchema.optional(),
    reason: z.string().optional(),
    decidedBy: z.string().optional(),
    connectionId: z.string().optional(),
});
export const foundryRequestDecisionSchema = z.object({
    decision: z.enum(["approve", "decline"]),
    note: z.string().trim().max(1000).optional(),
    connectionId: z.string().min(1).max(100).optional(),
});
// ── The agent catalogue ────────────────────────────────────────────────────────────────
const digest = z.string().regex(/^[0-9a-f]{64}$/i, "a SHA-256 digest, 64 hex characters");
export const foundryAgentDefinitionSchema = z.object({
    name: z.string().min(1).max(80),
    description: z.string().max(500).optional(),
    projectEndpoint: httpsUrl,
    agentName: z.string().min(1).max(200),
    agentVersion: z.string().min(1).max(100),
    definitionDigest: digest,
    instructionsDigest: digest,
    deployment: z.string().min(1).max(200),
    hostedTools: z.array(z.string().min(1).max(100)).max(50),
    tools: z.array(z.string().min(1).max(200)).max(200),
    classification: classificationSchema.optional(),
    /** Filled by AIOE from the signed-in person; whatever is sent is ignored. */
    owner: z.string().optional(),
    adopted: z.boolean().optional(),
    connectionId: z.string().min(1).max(100),
});
export const FOUNDRY_AGENT_ROLES = ["specialist", "member"];
export const foundryPublishRequestSchema = z.object({
    agentId: z
        .string()
        .regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,99}$/)
        .optional(),
    agent: foundryAgentDefinitionSchema,
    as: z.enum(FOUNDRY_AGENT_ROLES),
    notes: z.string().max(2000).optional(),
});
export const foundryPublishAnswerSchema = z.object({ agentId: z.string(), version: z.number().int().positive(), state: z.enum(["listed", "held"]) });
export const foundryReviewRequestSchema = z.object({ decision: z.enum(["approve", "reject"]), note: z.string().max(1000).optional() });
export const foundryWithdrawRequestSchema = z.object({ reason: z.string().max(1000).optional() });
export const foundryInstallRequestSchema = z.object({ as: z.enum(FOUNDRY_AGENT_ROLES), teamId: z.string().min(1).max(100).optional() });
export const foundryVersionSchema = z.object({
    version: z.number().int().positive(),
    state: z.enum(["listed", "held", "rejected"]),
    definition: foundryAgentDefinitionSchema,
    as: z.enum(FOUNDRY_AGENT_ROLES),
    notes: z.string().optional(),
    publishedAt: z.string(),
    publishedBy: z.string(),
    decidedAt: z.string().optional(),
    decidedBy: z.string().optional(),
    note: z.string().optional(),
});
export const foundryAgentListingSchema = z.object({
    agentId: z.string(),
    name: z.string(),
    description: z.string().optional(),
    latest: z.object({
        version: z.number().int().positive(),
        state: z.enum(["listed", "held", "rejected"]),
        definition: foundryAgentDefinitionSchema,
        as: z.enum(FOUNDRY_AGENT_ROLES),
        publishedAt: z.string(),
        publishedBy: z.string(),
    }),
    withdrawnAt: z.string().optional(),
    usedBy: z.number().int().nonnegative(),
});
export const FOUNDRY_PUBLISHING = ["open", "review", "admins"];
export const foundryCatalogueSchema = z.object({
    v: z.literal(1),
    agents: z.array(foundryAgentListingSchema),
    publishing: z.enum(FOUNDRY_PUBLISHING),
    canReview: z.boolean(),
});
//# sourceMappingURL=foundryCatalogue.js.map